import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowUpRight, Loader2, AlertCircle, Phone, Mail, MapPin, Globe } from "lucide-react";
import { toast } from "sonner";

import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { COMPANY } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const schema = z.object({
  name: z.string().min(2, "Please enter your name"),
  email: z.string().email("Please enter a valid email address"),
  subject: z.string().optional(),
  message: z.string().min(5, "Please enter a short message"),
});

const fieldBase =
  "w-full border border-border bg-white px-4 py-3 text-sm text-navy transition-colors duration-200 placeholder:text-steel/60 focus:border-brand-green focus:outline-none";
const labelBase = "mb-2 block font-display text-xs font-semibold uppercase tracking-wider text-navy";

const CONTACT_ITEMS = [
  { icon: Phone, label: "Phone", value: COMPANY.phone, href: COMPANY.phoneHref },
  { icon: Mail, label: "Email", value: COMPANY.email, href: COMPANY.emailHref },
  { icon: Globe, label: "Website", value: COMPANY.website, href: COMPANY.websiteHref },
  { icon: MapPin, label: "Address", value: COMPANY.address, href: null },
];

export default function Contact() {
  const [sent, setSent] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema) });

  const onSubmit = async (data) => {
    try {
      await axios.post(`${API}/contact`, data);
      setSent(true);
      reset();
      toast.success("Message sent.");
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <>
      <Seo
        title="Contact"
        description="Talk with the Precision Maintenance1 LLC team about your facility."
        path="/contact"
      />
      <PageHero eyebrow="Contact" title={["Talk with", "our team."]} />

      <section className="container-px mx-auto max-w-[1500px] py-20">
        <div className="grid gap-14 lg:grid-cols-[1fr_1.3fr]">
          <aside>
            <h2 className="font-display text-2xl font-bold tracking-tight text-navy">Contact details</h2>
            <div className="mt-8 flex flex-col gap-px overflow-hidden border border-border bg-border">
              {CONTACT_ITEMS.map((c) => (
                <div key={c.label} className="flex items-start gap-4 bg-white p-6">
                  <c.icon size={20} className="mt-0.5 flex-none text-brand-green" strokeWidth={1.7} />
                  <div>
                    <p className="overline text-steel">{c.label}</p>
                    {c.href ? (
                      <a
                        href={c.href}
                        data-testid={`contact-${c.label.toLowerCase()}`}
                        className="link-underline mt-1 inline-block text-sm text-navy"
                        {...(c.label === "Website" ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      >
                        {c.value}
                      </a>
                    ) : (
                      <p className="mt-1 text-sm text-navy">{c.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </aside>

          <div>
            {sent ? (
              <motion.div
                data-testid="contact-success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-start border border-brand-green/30 bg-brand-green/5 p-10 sm:p-14"
              >
                <CheckCircle2 size={44} className="text-brand-green" />
                <h2 className="mt-6 font-display text-3xl font-bold tracking-tight text-navy">Message sent.</h2>
                <p className="mt-4 max-w-lg text-base leading-relaxed text-steel">
                  Thank you for reaching out. Our team will respond shortly.
                </p>
                <button data-testid="contact-reset" onClick={() => setSent(false)} className="btn-ghost mt-8">
                  Send another message <ArrowUpRight size={16} />
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} data-testid="contact-form" noValidate className="grid gap-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  <label className="block">
                    <span className={labelBase}>Name <span className="text-brand-green-ink">*</span></span>
                    <input data-testid="contact-name" className={fieldBase} placeholder="Jane Doe" {...register("name")} />
                    {errors.name && <Err msg={errors.name.message} />}
                  </label>
                  <label className="block">
                    <span className={labelBase}>Email <span className="text-brand-green-ink">*</span></span>
                    <input data-testid="contact-email" type="email" className={fieldBase} placeholder="jane@company.com" {...register("email")} />
                    {errors.email && <Err msg={errors.email.message} />}
                  </label>
                </div>
                <label className="block">
                  <span className={labelBase}>Subject</span>
                  <input data-testid="contact-subject" className={fieldBase} placeholder="How can we help?" {...register("subject")} />
                </label>
                <label className="block">
                  <span className={labelBase}>Message <span className="text-brand-green-ink">*</span></span>
                  <textarea data-testid="contact-message" rows={6} className={fieldBase} placeholder="Tell us about your facility…" {...register("message")} />
                  {errors.message && <Err msg={errors.message.message} />}
                </label>
                <button type="submit" data-testid="contact-submit" disabled={isSubmitting} className="btn-primary w-full justify-center sm:w-fit disabled:opacity-60">
                  {isSubmitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Talk With Our Team <ArrowUpRight size={16} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

const Err = ({ msg }) => (
  <span className="mt-2 flex items-center gap-1.5 text-xs text-destructive" role="alert">
    <AlertCircle size={13} /> {msg}
  </span>
);
